import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Trash2, Upload, Video, X } from "lucide-react";
import { useEffect, useState } from "react";
import { showError, showSuccess } from "../../../utils/toastUtils";
import uploadFileToCloudinary from "../../../utils/uploadFileToCloudinary";

const INITIAL_DATA = {
  name: "",
  subCategoryId: "",
  duration: "",
  image: "",
  video: "",
  status: "active",

  extraCharges: [
    {
      title: "",
      amount: "",
    },
  ],
  imageUrl: "",
  videoUrl: "",

  pricingMode: "single",
  originalPrice: "",
  discountedPrice: "",

  discount: {
    type: "flat",
  },

  type: [
    {
      brand: "",
      originalPrice: "",
      discountedPrice: "",
    },
  ],

  description: [
    {
      heading: "",
      points: [""],
    },
  ],
  ratings: "",
};

const AddServiceDrawer = ({
  isOpen,
  onClose,
  onAddService,
  editingService,
}) => {
  const isEditMode = Boolean(editingService);
  const [formData, setFormData] = useState(INITIAL_DATA);
  const [subCategories, setSubCategories] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // useEffect(() => {
  //   console.log("editingService:", editingService);
  // }, [editingService]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setImagePreview(null);
      setVideoPreview(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !editingService || !subCategories.length) return;
    const hasTypes = editingService.type?.length > 0;

    setFormData({
      name: editingService.name || "",
      subCategoryId: editingService.subCategoryId?._id || "",
      duration: editingService.duration || "",
      image: "",
      video: "",
      status: editingService.status || "active",

      pricingMode: hasTypes ? "multiple" : "single",

      originalPrice: editingService.originalPrice ?? "",
      discountedPrice: editingService.discountedPrice ?? "",

      type: hasTypes
        ? editingService.type.map((t) => ({
            brand: t.brand,
            originalPrice: t.originalPrice,
            discountedPrice: t.discountedPrice,
          }))
        : [{ brand: "", originalPrice: "", discountedPrice: "" }],

      discount: editingService.discount || { type: "flat" },

      extraCharges: editingService.extraCharges?.length
        ? editingService.extraCharges
        : [{ title: "", amount: "" }],
      imageUrl: editingService.imageUrl || "",
      videoUrl: editingService.videoUrl || "",
      description:
        editingService.description?.length > 0
          ? editingService.description
          : [{ heading: "", points: [""] }],
      ratings:
        editingService.ratings !== undefined && editingService.ratings !== null
          ? String(editingService.ratings)
          : "",
    });

    setImagePreview(editingService.imageUrl || null);
    setVideoPreview(editingService.videoUrl || null);
  }, [isOpen, editingService, subCategories]);

  useEffect(() => {
    const fetchServices = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sub-category/get-all`,
      );

      // extract unique subcategories
      const map = new Map();
      response.data.data.forEach((s) => {
        if (s._id) {
          map.set(s._id, s.subCategoryName);
        }
      });

      setSubCategories([...map.entries()].map(([id, name]) => ({ id, name })));
    };

    fetchServices();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFormData((p) => ({ ...p, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFormData((p) => ({ ...p, video: file }));
    setVideoPreview(URL.createObjectURL(file));
  };
  // useEffect(() => {
  //   console.log("formData:", formData);
  // }, [formData]);

  useEffect(() => {
    const uploadMedia = async () => {
      // IMAGE
      if (formData.image) {
        setImageUploading(true);
        try {
          const { uploadPromise } = uploadFileToCloudinary(formData.image);
          const uploaded = await uploadPromise;
          // console.log("res:", res);
          if (!uploaded || !uploaded.url) {
            showError("Upload failed. Please try again.");
            return;
          }
          setFormData((p) => ({ ...p, imageUrl: uploaded.url }));
          setImagePreview(uploaded.url);
        } finally {
          setImageUploading(false);
        }
      }

      // VIDEO
      if (formData.video) {
        setVideoUploading(true);
        try {
          const { uploadPromise } = uploadFileToCloudinary(formData.video);
          const res = await uploadPromise;

          setFormData((p) => ({ ...p, videoUrl: res.url }));
          setVideoPreview(res.url);
        } finally {
          setVideoUploading(false);
        }
      }
    };

    uploadMedia();
  }, [formData.image, formData.video]);

  const validateForm = () => {
    const missing = [];

    if (
      formData.pricingMode === "single" &&
      (!formData.originalPrice || !formData.discountedPrice)
    ) {
      showError("Please enter original and discounted price");
      return false;
    }

    if (
      formData.pricingMode === "multiple" &&
      formData.type.some(
        (t) => !t.brand || !t.originalPrice || !t.discountedPrice,
      )
    ) {
      showError("Each brand must have original and discounted price");
      return false;
    }

    if (!formData.name?.trim()) missing.push("Service Name");
    if (!formData.subCategoryId) missing.push("Sub Category");
    if (!formData.duration) missing.push("Service Duration");
    if (!formData.status) missing.push("Service Status");
    if (!formData.imageUrl) missing.push("Service Image");

    if (missing.length) {
      showError(`Please fill out: ${missing[0]}`);
      return false;
    }

    return true;
  };
  const cleanedExtraCharges = formData.extraCharges.filter(
    (c) => c.title?.trim() && Number(c.amount) > 0,
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    validateForm();
    try {
      const payload = {
        name: formData.name.trim(),
        subCategoryId: formData.subCategoryId,
        duration: formData.duration,
        imageUrl: formData?.imageUrl,
        videoUrl: formData?.videoUrl,
        status: formData?.status,

        originalPrice:
          formData.pricingMode === "single"
            ? Number(formData.originalPrice)
            : null,

        discountedPrice:
          formData.pricingMode === "single"
            ? Number(formData.discountedPrice)
            : null,

        type:
          formData.pricingMode === "multiple"
            ? formData.type.map((t) => ({
                brand: t.brand.trim(),
                originalPrice: Number(t.originalPrice),
                discountedPrice: Number(t.discountedPrice),
              }))
            : [],

        discount: {
          type: formData.discount.type,
        },

        extraCharges: cleanedExtraCharges.length ? cleanedExtraCharges : [],

        description: formData.description
          .filter((d) => d.heading.trim())
          .map((d) => ({
            heading: d.heading.trim(),
            points: d.points.filter((p) => p.trim()),
          })),
        ratings:
          formData.ratings === ""
            ? 0
            : Math.min(5, Math.max(0, Number(formData.ratings))),
      };

      const url = isEditMode
        ? `${process.env.REACT_APP_API_URL}/services/update/${editingService._id}`
        : `${process.env.REACT_APP_API_URL}/services/create`;

      const method = isEditMode ? "put" : "post";

      const res = await axios[method](url, payload);

      onAddService();
      showSuccess(
        res?.data?.message ||
          (isEditMode
            ? "Service Updated Successfully"
            : "Service Created Successfully"),
      );
      onClose();
      resetForm();
    } catch (err) {
      console.error(err);
      showError(
        err?.response?.data?.message ||
          (isEditMode
            ? "Failed to update service"
            : "Failed to create service"),
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_DATA);
    setImagePreview(null);
    setVideoPreview(null);
  };

  const getDiscountPreview = () => {
    // Only for single-price services
    if (formData.pricingMode !== "single") return null;

    const original = Number(formData.originalPrice);
    const discounted = Number(formData.discountedPrice);

    // Allow typing but avoid junk values
    if (!original || !discounted) return null;
    if (original <= 0 || discounted <= 0) return null;

    const diff = original - discounted;

    // No preview for invalid discount
    if (diff <= 0) return null;

    // Percentage discount
    if (formData.discount.type === "percentage") {
      const percent = (diff / original) * 100;

      // Show clean decimals only when needed
      const formatted =
        percent % 1 === 0 ? percent.toFixed(0) : percent.toFixed(0);

      return `${formatted}% OFF`;
    }

    // Flat discount
    return `₹${Math.round(diff)} OFF`;
  };

  const getBrandDiscountPreview = (originalPrice, discountedPrice) => {
    const original = Number(originalPrice);
    const discounted = Number(discountedPrice);

    if (!original || !discounted) return null;
    if (original <= 0 || discounted <= 0) return null;

    const diff = original - discounted;
    if (diff <= 0) return null;

    if (formData.discount.type === "percentage") {
      const percent = (diff / original) * 100;
      const formatted =
        percent % 1 === 0 ? percent.toFixed(0) : percent.toFixed(0);

      return `${formatted}% OFF`;
    }

    return `₹${Math.round(diff)} OFF`;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          />

          {/* Drawer */}
          <motion.div
            className="fixed top-0 right-0 h-full w-96 md:w-[500px] lg:w-[600px] bg-white z-50 shadow-2xl overflow-hidden"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-[15px] border-b bg-brandAccentColor outline-none">
              <h2 className="text-lg font-bold text-primaryTextColor">
                {isEditMode ? "Edit Service" : "Add New Service"}
              </h2>

              <button
                type="button"
                onClick={onClose}
                className=" rounded-full p-2 transition"
              >
                <X size={26} />
              </button>
            </div>

            {/* Form Body */}
            <form
              onSubmit={handleSubmit}
              className="px-6 pt-6 space-y-6 overflow-y-auto h-[calc(100vh-80px)]"
            >
              {/* Service Name */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  Service Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Classic Facial"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  Sub-Category
                </label>
                <select
                  name="subCategoryId"
                  value={formData.subCategoryId}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
                >
                  <option value="" disabled>
                    Select a category
                  </option>
                  {subCategories?.map((subCat) => {
                    return (
                      <option key={subCat?.id} value={subCat?.id}>
                        {subCat?.name}
                      </option>
                    );
                  })}
                </select>
              </div>
              {/* Duration */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor">
                  Status :
                </label>

                <div className="flex gap-6">
                  {["active", "inactive"].map((status) => (
                    <label
                      key={status}
                      className="flex items-center gap-1 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="status"
                        value={status}
                        checked={formData.status === status}
                        onChange={handleChange}
                        className="size-4"
                      />
                      <span className="capitalize text-sm">{status}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  Duration (e.g- 1hr 35mins)
                </label>
                <input
                  type="text"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                  min="1"
                  step="1"
                  placeholder="60 mins"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
                />
              </div>
              {/* RATINGS */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  Ratings (0 – 5)
                </label>

                <input
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={formData.ratings}
                  onChange={(e) => {
                    setFormData((p) => ({
                      ...p,
                      ratings: e.target.value,
                    }));
                  }}
                  placeholder="e.g. 4.5"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
                />

                <p className="text-xs text-gray-500 mt-1">
                  Used for display purposes. Can be updated later.
                </p>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  Pricing
                </label>
                <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
                  {/* Pricing Mode Tabs */}
                  <div className="flex border-b bg-gray-50">
                    {[
                      { label: "Single Price", value: "single" },
                      { label: "Brand-wise Pricing", value: "multiple" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() =>
                          setFormData((p) => ({
                            ...p,
                            pricingMode: opt.value,
                            originalPrice: "",
                            discountedPrice: "",
                            type: [
                              {
                                brand: "",
                                originalPrice: "",
                                discountedPrice: "",
                              },
                            ],
                          }))
                        }
                        className={` flex-1 px-4 py-3 text-sm font-semibold transition-all ${formData.pricingMode === opt.value ? "bg-primaryColor/90 text-white" : "text-gray-600 hover:bg-gray-100"} `}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Card Body */}
                  <div className="p-5 space-y-5">
                    {/* Single Price Mode */}
                    {formData.pricingMode === "single" && (
                      <div className="pt-2">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-bold text-primaryTextColor mb-2 block">
                              Original Price
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                                ₹
                              </span>
                              <input
                                type="number"
                                min={0}
                                value={formData.originalPrice}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    originalPrice: e.target.value,
                                  }))
                                }
                                required
                                placeholder="0"
                                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="text-sm font-bold text-primaryTextColor mb-2 block">
                              Discounted Price
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                                ₹
                              </span>
                              <input
                                type="number"
                                min={0}
                                value={formData.discountedPrice}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    discountedPrice: e.target.value,
                                  }))
                                }
                                required
                                placeholder="0"
                                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor"
                              />
                            </div>
                          </div>
                        </div>
                        {getDiscountPreview() && (
                          <div className="mt-3 text-sm font-semibold text-emerald-700">
                            Customer sees: {getDiscountPreview()}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Brand-wise Pricing Mode */}
                    {formData.pricingMode === "multiple" && (
                      <div className="pt-2">
                        <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-3">
                          Brand Pricing List
                        </label>

                        <div className="space-y-3">
                          {formData?.type?.map((item, index) => (
                            <div key={index} className="flex flex-col gap-2">
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  placeholder="Brand name"
                                  value={item.brand}
                                  required
                                  onChange={(e) => {
                                    const updated = [...formData.type];
                                    updated[index].brand = e.target.value;
                                    setFormData((prev) => ({
                                      ...prev,
                                      type: updated,
                                    }));
                                  }}
                                  className="flex-1 px-4 py-2 border text-sm border-gray-300 rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none bg-white"
                                />

                                <input
                                  type="number"
                                  min={0}
                                  placeholder="Original"
                                  value={item.originalPrice}
                                  required
                                  onChange={(e) => {
                                    const updated = [...formData.type];
                                    updated[index].originalPrice =
                                      e.target.value;
                                    setFormData((p) => ({
                                      ...p,
                                      type: updated,
                                    }));
                                  }}
                                  className="w-32 px-3 py-2 border text-sm border-gray-300 rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none bg-white"
                                />

                                <input
                                  type="number"
                                  min={0}
                                  placeholder="Discounted"
                                  value={item.discountedPrice}
                                  required
                                  onChange={(e) => {
                                    const updated = [...formData.type];
                                    updated[index].discountedPrice =
                                      e.target.value;
                                    setFormData((p) => ({
                                      ...p,
                                      type: updated,
                                    }));
                                  }}
                                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-accentTextColor outline-none bg-white"
                                />
                                {formData.type?.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = formData.type.filter(
                                        (_, i) => i !== index,
                                      );
                                      setFormData((prev) => ({
                                        ...prev,
                                        type: updated,
                                      }));
                                    }}
                                    className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </div>

                              {getBrandDiscountPreview(
                                item.originalPrice,
                                item.discountedPrice,
                              ) && (
                                <div className="mt-1 ml-2 flex items-center gap-1 text-xs font-semibold text-emerald-700">
                                  <span>Customer sees:</span>
                                  <span>
                                    {getBrandDiscountPreview(
                                      item.originalPrice,
                                      item.discountedPrice,
                                    )}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}

                          <button
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                type: [
                                  ...prev.type,
                                  {
                                    brand: "",
                                    originalPrice: "",
                                    discountedPrice: "",
                                  },
                                ],
                              }))
                            }
                            className="text-sm text-accentTextColor hover:underline font-medium"
                          >
                            + Add another brand
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-4">
                      <label className="text-sm font-semibold text-primaryTextColor whitespace-nowrap">
                        Discount Type
                      </label>

                      <div className="flex gap-2">
                        {[
                          { label: "Flat ₹", value: "flat" },
                          { label: "% Percentage", value: "percentage" },
                        ].map((opt) => {
                          const active = formData.discount.type === opt.value;

                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() =>
                                setFormData((p) => ({
                                  ...p,
                                  discount: { type: opt.value },
                                }))
                              }
                              className={` px-3 py-1.5 text-xs font-semibold rounded-full border transition-all ${active ? "bg-primaryBtnColor text-white border-primaryBtnColor shadow-sm" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"} `}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Extra Charges */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  Extra Charges
                </label>

                <div className="space-y-4">
                  {formData?.extraCharges?.map((item, index) => (
                    <div key={index} className="flex gap-3 items-center">
                      <input
                        type="text"
                        placeholder="Charge title"
                        value={item.title}
                        onChange={(e) => {
                          const updated = [...formData.extraCharges];
                          updated[index].title = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            extraCharges: updated,
                          }));
                        }}
                        className="flex-1 px-4 py-2 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
                      />

                      <input
                        type="number"
                        placeholder="Amount"
                        value={item.amount}
                        onChange={(e) => {
                          const updated = [...formData.extraCharges];
                          updated[index].amount = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            extraCharges: updated,
                          }));
                        }}
                        className="w-32 px-4 py-2 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
                      />

                      {formData?.extraCharges.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = formData.extraCharges.filter(
                              (_, i) => i !== index,
                            );
                            setFormData((prev) => ({
                              ...prev,
                              extraCharges: updated,
                            }));
                          }}
                          className="text-red-600 hover:bg-red-50 p-2 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        extraCharges: [
                          ...prev.extraCharges,
                          { title: "", amount: "" },
                        ],
                      }))
                    }
                    className="text-sm text-accentTextColor outline-none hover:underline"
                  >
                    + Add extra charge
                  </button>
                </div>
              </div>
              {/* DESCRIPTION */}
              <div>
                <label className="text-sm font-bold text-primaryTextColor mb-2 block">
                  Service Description
                </label>

                <div className="space-y-6">
                  {formData.description.map((section, secIndex) => (
                    <div
                      key={secIndex}
                      className="border rounded-lg p-4 space-y-3"
                    >
                      {/* Heading */}
                      <input
                        type="text"
                        placeholder="Section Heading (e.g. What’s Included)"
                        value={section.heading}
                        onChange={(e) => {
                          const updated = [...formData.description];
                          updated[secIndex].heading = e.target.value;
                          setFormData((p) => ({ ...p, description: updated }));
                        }}
                        className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-accentTextColor outline-none"
                      />

                      {/* Points */}
                      {section.points.map((point, pointIndex) => (
                        <div
                          key={pointIndex}
                          className="flex gap-2 items-center"
                        >
                          <input
                            type="text"
                            placeholder="Bullet point"
                            value={point}
                            onChange={(e) => {
                              const updated = [...formData.description];
                              updated[secIndex].points[pointIndex] =
                                e.target.value;
                              setFormData((p) => ({
                                ...p,
                                description: updated,
                              }));
                            }}
                            className="flex-1 px-3 py-2 border rounded-md focus:ring-1 focus:ring-accentTextColor outline-none"
                          />

                          {section.points.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...formData.description];
                                updated[secIndex].points = updated[
                                  secIndex
                                ].points.filter((_, i) => i !== pointIndex);
                                setFormData((p) => ({
                                  ...p,
                                  description: updated,
                                }));
                              }}
                              className="text-red-600"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}

                      <div className="flex w-full justify-between items-center">
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...formData.description];
                            updated[secIndex].points.push("");
                            setFormData((p) => ({
                              ...p,
                              description: updated,
                            }));
                          }}
                          className="text-sm text-accentTextColor"
                        >
                          + Add point
                        </button>

                        {/* Remove section */}
                        {formData.description.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const updated = formData.description.filter(
                                (_, i) => i !== secIndex,
                              );
                              setFormData((p) => ({
                                ...p,
                                description: updated,
                              }));
                            }}
                            className="text-sm text-red-600 "
                          >
                            Remove section
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({
                        ...p,
                        description: [
                          ...p.description,
                          { heading: "", points: [""] },
                        ],
                      }))
                    }
                    className="text-sm text-accentTextColor"
                  >
                    + Add description section
                  </button>
                </div>
              </div>

              <div>
                {/* Image Upload */}
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  Service Image{" "}
                </label>
                <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {imagePreview ? (
                    <div className="relative inline-block">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="mx-auto max-h-48 rounded-lg object-cover"
                      />

                      {imageUploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                          <Loader2
                            className="animate-spin text-white"
                            size={32}
                          />
                        </div>
                      )}

                      {!imageUploading && (
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview(null);
                            setFormData((p) => ({
                              ...p,
                              image: null,
                              imageUrl: "",
                            }));
                          }}
                          className="mt-3 text-sm text-red-600 hover:underline flex items-center gap-1 mx-auto"
                        >
                          <Trash2 size={16} /> Remove
                        </button>
                      )}
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <Upload
                        size={32}
                        className="mx-auto mb-3 text-gray-400"
                      />
                      <p className="text-sm text-gray-600">
                        Click to upload image
                      </p>
                    </label>
                  )}
                </div>
              </div>
              {/* Video Upload */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  Service Video{" "}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  {videoPreview ? (
                    <div className="relative">
                      <video
                        src={videoPreview}
                        controls
                        className="mx-auto max-h-64 rounded-lg w-full"
                      />

                      {videoUploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                          <Loader2
                            className="animate-spin text-white"
                            size={32}
                          />
                        </div>
                      )}

                      {!videoUploading && (
                        <button
                          type="button"
                          onClick={() => {
                            setVideoPreview(null);
                            setFormData((p) => ({
                              ...p,
                              video: null,
                              videoUrl: "",
                            }));
                          }}
                          className="mt-3 text-sm text-red-600 hover:underline flex items-center gap-1 mx-auto"
                        >
                          <Trash2 size={16} /> Remove Video
                        </button>
                      )}
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleVideoChange}
                        className="hidden"
                      />
                      <Video size={32} className="mx-auto mb-3 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        Click to upload video
                      </p>
                    </label>
                  )}
                </div>
              </div>
              {/* Buttons */}
              <div className="flex gap-4 py-4 border-t sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={imageUploading || videoUploading}
                  className="relative flex-1 px-6 py-3 bg-primaryBtnColor text-white font-medium rounded-lg hover:bg-primaryBtnHoverColor transition shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="animate-spin" size={20} />
                    </span>
                  ) : isEditMode ? (
                    "Update Service"
                  ) : (
                    "Add Service"
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddServiceDrawer;
// import axios from "axios";
// import { motion, AnimatePresence } from "framer-motion";
// import { Loader2, Trash2, Upload, Video, X } from "lucide-react";
// import { useEffect, useState } from "react";
// import { showError, showSuccess } from "../../../utils/toastUtils";
// import uploadFileToCloudinary from "../../../utils/uploadFileToCloudinary";

// const INITIAL_DATA = {
//   name: "",
//   subCategoryId: "",
//   duration: "",
//   image: "",
//   video: "",
//   status: "active",
//   basePrice: "",
//   pricingMode: "single",
//   type: [
//     {
//       brand: "",
//       price: "",
//     },
//   ],
//   extraCharges: [
//     {
//       title: "",
//       amount: "",
//     },
//   ],
//   imageUrl: "",
//   videoUrl: "",

//   discount: {
//     type: "",
//     value: "",
//   },

//   description: [
//     {
//       heading: "",
//       points: [""],
//     },
//   ],
//   ratings: "",
// };

// const AddServiceDrawer = ({
//   isOpen,
//   onClose,
//   onAddService,
//   editingService,
// }) => {
//   const isEditMode = Boolean(editingService);
//   const [formData, setFormData] = useState(INITIAL_DATA);
//   const [subCategories, setSubCategories] = useState([]);
//   const [imageUploading, setImageUploading] = useState(false);
//   const [videoUploading, setVideoUploading] = useState(false);
//   const [imagePreview, setImagePreview] = useState(null);
//   const [videoPreview, setVideoPreview] = useState(null);
//   const [loading, setLoading] = useState(false);

//   // useEffect(() => {
//   //   console.log("editingService:", editingService);
//   // }, [editingService]);

//   useEffect(() => {
//     if (!isOpen) {
//       resetForm();
//       setImagePreview(null);
//       setVideoPreview(null);
//     }
//   }, [isOpen]);

//   useEffect(() => {
//     if (!isOpen || !editingService || !subCategories.length) return;
//     const hasTypes = editingService.type?.length > 0;

//     setFormData({
//       name: editingService.name || "",
//       subCategoryId: editingService.subCategoryId?._id || "",
//       duration: editingService.duration || "",
//       image: "",
//       video: "",
//       status: editingService.status || "active",
//       pricingMode: hasTypes ? "multiple" : "single",
//       basePrice: editingService.basePrice || "",
//       type: hasTypes ? editingService.type : [{ brand: "", price: "" }],

//       extraCharges: editingService.extraCharges?.length
//         ? editingService.extraCharges
//         : [{ title: "", amount: "" }],
//       imageUrl: editingService.imageUrl || "",
//       videoUrl: editingService.videoUrl || "",
//       discount: editingService.discount || { type: "", value: "" },
//       description:
//         editingService.description?.length > 0
//           ? editingService.description
//           : [{ heading: "", points: [""] }],
//       ratings:
//         editingService.ratings !== undefined && editingService.ratings !== null
//           ? String(editingService.ratings)
//           : "",
//     });

//     setImagePreview(editingService.imageUrl || null);
//     setVideoPreview(editingService.videoUrl || null);
//   }, [isOpen, editingService, subCategories]);

//   useEffect(() => {
//     const fetchServices = async () => {
//       const response = await axios.get(
//         `${process.env.REACT_APP_API_URL}/sub-category/get-all`
//       );

//       // extract unique subcategories
//       const map = new Map();
//       response.data.data.forEach((s) => {
//         if (s._id) {
//           map.set(s._id, s.subCategoryName);
//         }
//       });

//       setSubCategories([...map.entries()].map(([id, name]) => ({ id, name })));
//     };

//     fetchServices();
//   }, []);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((p) => ({ ...p, [name]: value }));
//   };

//   const handleImageChange = (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     setFormData((p) => ({ ...p, image: file }));
//     setImagePreview(URL.createObjectURL(file));
//   };

//   const handleVideoChange = (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     setFormData((p) => ({ ...p, video: file }));
//     setVideoPreview(URL.createObjectURL(file));
//   };
//   // useEffect(() => {
//   //   console.log("formData:", formData);
//   // }, [formData]);

//   useEffect(() => {
//     const uploadMedia = async () => {
//       // IMAGE
//       if (formData.image) {
//         setImageUploading(true);
//         try {
//           const { uploadPromise } = uploadFileToCloudinary(formData.image);
//           const uploaded = await uploadPromise;
//           // console.log("res:", res);
//           if (!uploaded || !uploaded.url) {
//             showError("Upload failed. Please try again.");
//             return;
//           }
//           setFormData((p) => ({ ...p, imageUrl: uploaded.url }));
//           setImagePreview(uploaded.url);
//         } finally {
//           setImageUploading(false);
//         }
//       }

//       // VIDEO
//       if (formData.video) {
//         setVideoUploading(true);
//         try {
//           const { uploadPromise } = uploadFileToCloudinary(formData.video);
//           const res = await uploadPromise;

//           setFormData((p) => ({ ...p, videoUrl: res.url }));
//           setVideoPreview(res.url);
//         } finally {
//           setVideoUploading(false);
//         }
//       }
//     };

//     uploadMedia();
//   }, [formData.image, formData.video]);

//   const validateForm = () => {
//     const missing = [];

//     if (formData.pricingMode === "single" && !formData.basePrice) {
//       showError("Please enter service price");
//       return false;
//     }

//     if (formData.pricingMode === "multiple" && !formData.type?.[0]?.brand) {
//       showError("Please add at least one service type");
//       return false;
//     }

//     if (!formData.name?.trim()) missing.push("Service Name");
//     if (!formData.subCategoryId) missing.push("Sub Category");
//     if (!formData.duration) missing.push("Service Duration");
//     if (!formData.status) missing.push("Service Status");
//     if (!formData.imageUrl) missing.push("Service Image");

//     if (missing.length) {
//       showError(`Please fill out: ${missing[0]}`);
//       return false;
//     }

//     return true;
//   };
//   const cleanedExtraCharges = formData.extraCharges.filter(
//     (c) => c.title?.trim() && Number(c.amount) > 0
//   );

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     validateForm();
//     try {
//       const payload = {
//         name: formData.name.trim(),
//         subCategoryId: formData.subCategoryId,
//         duration: formData.duration,
//         imageUrl: formData?.imageUrl,
//         videoUrl: formData?.videoUrl,
//         status: formData?.status,
//         basePrice:
//           formData.pricingMode === "single" ? Number(formData.basePrice) : null,

//         type: formData.pricingMode === "multiple" ? formData.type : [],
//         extraCharges: cleanedExtraCharges.length ? cleanedExtraCharges : [],
//         discount:
//           formData.discount?.type && formData.discount.value
//             ? {
//                 type: formData.discount.type,
//                 value: Number(formData.discount.value),
//               }
//             : null,

//         description: formData.description
//           .filter((d) => d.heading.trim())
//           .map((d) => ({
//             heading: d.heading.trim(),
//             points: d.points.filter((p) => p.trim()),
//           })),
//         ratings:
//           formData.ratings === ""
//             ? 0
//             : Math.min(5, Math.max(0, Number(formData.ratings))),
//       };

//       const url = isEditMode
//         ? `${process.env.REACT_APP_API_URL}/services/update/${editingService._id}`
//         : `${process.env.REACT_APP_API_URL}/services/create`;

//       const method = isEditMode ? "put" : "post";

//       const res = await axios[method](url, payload);

//       onAddService();
//       showSuccess(
//         res?.data?.message ||
//           (isEditMode
//             ? "Service Updated Successfully"
//             : "Service Created Successfully")
//       );
//       onClose();
//       resetForm();
//     } catch (err) {
//       console.error(err);
//       showError(
//         err?.response?.data?.message ||
//           (isEditMode ? "Failed to update service" : "Failed to create service")
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   const resetForm = () => {
//     setFormData(INITIAL_DATA);
//     setImagePreview(null);
//     setVideoPreview(null);
//   };

//   if (!isOpen) return null;

//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <>
//           {/* Backdrop */}
//           <motion.div
//             className="fixed inset-0 bg-black/50 z-40"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             transition={{ duration: 0.25 }}
//           />

//           {/* Drawer */}
//           <motion.div
//             className="fixed top-0 right-0 h-full w-96 md:w-[500px] lg:w-[600px] bg-white z-50 shadow-2xl overflow-hidden"
//             initial={{ x: "100%" }}
//             animate={{ x: 0 }}
//             exit={{ x: "100%" }}
//             transition={{
//               type: "spring",
//               stiffness: 300,
//               damping: 30,
//             }}
//           >
//             {/* Header */}
//             <div className="flex items-center justify-between p-[15px] border-b bg-brandAccentColor outline-none">
//               <h2 className="text-lg font-bold text-primaryTextColor">
//                 {isEditMode ? "Edit Service" : "Add New Service"}
//               </h2>

//               <button
//                 type="button"
//                 onClick={onClose}
//                 className=" rounded-full p-2 transition"
//               >
//                 <X size={26} />
//               </button>
//             </div>

//             {/* Form Body */}
//             <form
//               onSubmit={handleSubmit}
//               className="px-6 pt-6 space-y-6 overflow-y-auto h-[calc(100vh-80px)]"
//             >
//               {/* Service Name */}
//               <div>
//                 <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
//                   Service Name
//                 </label>
//                 <input
//                   type="text"
//                   name="name"
//                   value={formData.name}
//                   onChange={handleChange}
//                   required
//                   placeholder="e.g., Classic Facial"
//                   className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
//                 />
//               </div>
//               <div>
//                 <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
//                   Sub-Category
//                 </label>
//                 <select
//                   name="subCategoryId"
//                   value={formData.subCategoryId}
//                   onChange={handleChange}
//                   required
//                   className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
//                 >
//                   <option value="" disabled>
//                     Select a category
//                   </option>
//                   {subCategories?.map((subCat) => {
//                     return (
//                       <option key={subCat?.id} value={subCat?.id}>
//                         {subCat?.name}
//                       </option>
//                     );
//                   })}
//                 </select>
//               </div>
//               {/* Duration */}
//               <div className="flex items-center gap-4">
//                 <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor">
//                   Status :
//                 </label>

//                 <div className="flex gap-6">
//                   {["active", "inactive"].map((status) => (
//                     <label
//                       key={status}
//                       className="flex items-center gap-1 cursor-pointer"
//                     >
//                       <input
//                         type="radio"
//                         name="status"
//                         value={status}
//                         checked={formData.status === status}
//                         onChange={handleChange}
//                         className="size-4"
//                       />
//                       <span className="capitalize text-sm">{status}</span>
//                     </label>
//                   ))}
//                 </div>
//               </div>
//               <div>
//                 <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
//                   Duration (e.g- 1hr 35mins)
//                 </label>
//                 <input
//                   type="text"
//                   name="duration"
//                   value={formData.duration}
//                   onChange={handleChange}
//                   required
//                   min="1"
//                   step="1"
//                   placeholder="60 mins"
//                   className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
//                 />
//               </div>
//               {/* RATINGS */}
//               <div>
//                 <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
//                   Ratings (0 – 5)
//                 </label>

//                 <input
//                   type="number"
//                   min="0"
//                   max="5"
//                   step="0.1"
//                   value={formData.ratings}
//                   onChange={(e) => {
//                     setFormData((p) => ({
//                       ...p,
//                       ratings: e.target.value,
//                     }));
//                   }}
//                   placeholder="e.g. 4.5"
//                   className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
//                 />

//                 <p className="text-xs text-gray-500 mt-1">
//                   Used for display purposes. Can be updated later.
//                 </p>
//               </div>

//               {/* Pricing Mode */}
//               <div className="flex items-center gap-4">
//                 <label className="text-sm font-bold text-primaryTextColor ">
//                   Pricing Type :
//                 </label>

//                 <div className="flex gap-6">
//                   {[
//                     { label: "Single Price", value: "single" },
//                     { label: "Brand-wise Pricing", value: "multiple" },
//                   ].map((opt) => (
//                     <label
//                       key={opt.value}
//                       className="flex items-center gap-2 cursor-pointer"
//                     >
//                       <input
//                         type="radio"
//                         name="pricingMode"
//                         value={opt.value}
//                         checked={formData.pricingMode === opt.value}
//                         onChange={(e) =>
//                           setFormData((p) => ({
//                             ...p,
//                             pricingMode: e.target.value,
//                             basePrice: "",
//                             type: [{ brand: "", price: "" }],
//                           }))
//                         }
//                         className="size-4"
//                       />
//                       <span className="text-sm">{opt.label}</span>
//                     </label>
//                   ))}
//                 </div>
//               </div>
//               {formData.pricingMode === "single" && (
//                 <div>
//                   <label className="text-sm font-bold text-primaryTextColor mb-2 block">
//                     Service Price
//                   </label>
//                   <input
//                     type="number"
//                     name="basePrice"
//                     value={formData.basePrice}
//                     onChange={handleChange}
//                     required={formData?.pricingMode === "single"}
//                     placeholder="e.g. 499"
//                     className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
//                   />
//                 </div>
//               )}
//               {/* DISCOUNT */}
//               <div>
//                 <label className="text-sm font-bold text-primaryTextColor mb-2 block">
//                   Discount
//                 </label>

//                 <div className="flex gap-6 mb-3">
//                   {[
//                     { label: "No Discount", value: "" },
//                     { label: "Percentage (%)", value: "percentage" },
//                     { label: "Flat (₹)", value: "flat" },
//                   ].map((opt) => (
//                     <label key={opt.value} className="flex items-center gap-2">
//                       <input
//                         type="radio"
//                         value={opt.value}
//                         checked={formData.discount.type === opt.value}
//                         onChange={(e) =>
//                           setFormData((p) => ({
//                             ...p,
//                             discount: { type: e.target.value, value: "" },
//                           }))
//                         }
//                       />
//                       <span className="text-sm">{opt.label}</span>
//                     </label>
//                   ))}
//                 </div>

//                 {formData.discount.type && (
//                   <input
//                     type="number"
//                     min="0"
//                     placeholder={
//                       formData.discount.type === "percentage"
//                         ? "e.g. 20"
//                         : "e.g. 100"
//                     }
//                     value={formData.discount.value}
//                     onChange={(e) =>
//                       setFormData((p) => ({
//                         ...p,
//                         discount: {
//                           ...p.discount,
//                           value: Number(e.target.value),
//                         },
//                       }))
//                     }
//                     className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
//                   />
//                 )}
//               </div>

//               {/* Service Types */}
//               {formData.pricingMode === "multiple" && (
//                 <div>
//                   <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
//                     Service Types (Brand Pricing)
//                   </label>

//                   <div className="space-y-4">
//                     {formData?.type?.map((item, index) => (
//                       <div key={index} className="flex gap-3 items-center">
//                         <input
//                           type="text"
//                           placeholder="Brand name"
//                           value={item.brand}
//                           required
//                           onChange={(e) => {
//                             const updated = [...formData.type];
//                             updated[index].brand = e.target.value;
//                             setFormData((prev) => ({ ...prev, type: updated }));
//                           }}
//                           className="flex-1 px-4 py-2 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
//                         />

//                         <input
//                           type="number"
//                           placeholder="Price"
//                           required
//                           value={item.price}
//                           onChange={(e) => {
//                             const updated = [...formData.type];
//                             updated[index].price = e.target.value;
//                             setFormData((prev) => ({ ...prev, type: updated }));
//                           }}
//                           className="w-32 px-4 py-2 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
//                         />

//                         {formData.type?.length > 1 && (
//                           <button
//                             type="button"
//                             onClick={() => {
//                               const updated = formData.type.filter(
//                                 (_, i) => i !== index
//                               );
//                               setFormData((prev) => ({
//                                 ...prev,
//                                 type: updated,
//                               }));
//                             }}
//                             className="text-red-600 hover:bg-red-50 p-2 rounded-lg"
//                           >
//                             <Trash2 size={16} />
//                           </button>
//                         )}
//                       </div>
//                     ))}

//                     <button
//                       type="button"
//                       onClick={() =>
//                         setFormData((prev) => ({
//                           ...prev,
//                           type: [...prev.type, { brand: "", price: "" }],
//                         }))
//                       }
//                       className="text-sm text-accentTextColor hover:underline"
//                     >
//                       + Add another brand
//                     </button>
//                   </div>
//                 </div>
//               )}
//               {/* Extra Charges */}
//               <div>
//                 <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
//                   Extra Charges
//                 </label>

//                 <div className="space-y-4">
//                   {formData?.extraCharges?.map((item, index) => (
//                     <div key={index} className="flex gap-3 items-center">
//                       <input
//                         type="text"
//                         placeholder="Charge title"
//                         value={item.title}
//                         onChange={(e) => {
//                           const updated = [...formData.extraCharges];
//                           updated[index].title = e.target.value;
//                           setFormData((prev) => ({
//                             ...prev,
//                             extraCharges: updated,
//                           }));
//                         }}
//                         className="flex-1 px-4 py-2 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
//                       />

//                       <input
//                         type="number"
//                         placeholder="Amount"
//                         value={item.amount}
//                         onChange={(e) => {
//                           const updated = [...formData.extraCharges];
//                           updated[index].amount = e.target.value;
//                           setFormData((prev) => ({
//                             ...prev,
//                             extraCharges: updated,
//                           }));
//                         }}
//                         className="w-32 px-4 py-2 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
//                       />

//                       {formData?.extraCharges.length > 1 && (
//                         <button
//                           type="button"
//                           onClick={() => {
//                             const updated = formData.extraCharges.filter(
//                               (_, i) => i !== index
//                             );
//                             setFormData((prev) => ({
//                               ...prev,
//                               extraCharges: updated,
//                             }));
//                           }}
//                           className="text-red-600 hover:bg-red-50 p-2 rounded-lg"
//                         >
//                           <Trash2 size={16} />
//                         </button>
//                       )}
//                     </div>
//                   ))}

//                   <button
//                     type="button"
//                     onClick={() =>
//                       setFormData((prev) => ({
//                         ...prev,
//                         extraCharges: [
//                           ...prev.extraCharges,
//                           { title: "", amount: "" },
//                         ],
//                       }))
//                     }
//                     className="text-sm text-accentTextColor outline-none hover:underline"
//                   >
//                     + Add extra charge
//                   </button>
//                 </div>
//               </div>
//               {/* DESCRIPTION */}
//               <div>
//                 <label className="text-sm font-bold text-primaryTextColor mb-2 block">
//                   Service Description
//                 </label>

//                 <div className="space-y-6">
//                   {formData.description.map((section, secIndex) => (
//                     <div
//                       key={secIndex}
//                       className="border rounded-lg p-4 space-y-3"
//                     >
//                       {/* Heading */}
//                       <input
//                         type="text"
//                         placeholder="Section Heading (e.g. What’s Included)"
//                         value={section.heading}
//                         onChange={(e) => {
//                           const updated = [...formData.description];
//                           updated[secIndex].heading = e.target.value;
//                           setFormData((p) => ({ ...p, description: updated }));
//                         }}
//                         className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-accentTextColor outline-none"
//                       />

//                       {/* Points */}
//                       {section.points.map((point, pointIndex) => (
//                         <div
//                           key={pointIndex}
//                           className="flex gap-2 items-center"
//                         >
//                           <input
//                             type="text"
//                             placeholder="Bullet point"
//                             value={point}
//                             onChange={(e) => {
//                               const updated = [...formData.description];
//                               updated[secIndex].points[pointIndex] =
//                                 e.target.value;
//                               setFormData((p) => ({
//                                 ...p,
//                                 description: updated,
//                               }));
//                             }}
//                             className="flex-1 px-3 py-2 border rounded-md focus:ring-1 focus:ring-accentTextColor outline-none"
//                           />

//                           {section.points.length > 1 && (
//                             <button
//                               type="button"
//                               onClick={() => {
//                                 const updated = [...formData.description];
//                                 updated[secIndex].points = updated[
//                                   secIndex
//                                 ].points.filter((_, i) => i !== pointIndex);
//                                 setFormData((p) => ({
//                                   ...p,
//                                   description: updated,
//                                 }));
//                               }}
//                               className="text-red-600"
//                             >
//                               ✕
//                             </button>
//                           )}
//                         </div>
//                       ))}

//                       <div className="flex w-full justify-between items-center">
//                         <button
//                           type="button"
//                           onClick={() => {
//                             const updated = [...formData.description];
//                             updated[secIndex].points.push("");
//                             setFormData((p) => ({
//                               ...p,
//                               description: updated,
//                             }));
//                           }}
//                           className="text-sm text-accentTextColor"
//                         >
//                           + Add point
//                         </button>

//                         {/* Remove section */}
//                         {formData.description.length > 1 && (
//                           <button
//                             type="button"
//                             onClick={() => {
//                               const updated = formData.description.filter(
//                                 (_, i) => i !== secIndex
//                               );
//                               setFormData((p) => ({
//                                 ...p,
//                                 description: updated,
//                               }));
//                             }}
//                             className="text-sm text-red-600 "
//                           >
//                             Remove section
//                           </button>
//                         )}
//                       </div>
//                     </div>
//                   ))}

//                   <button
//                     type="button"
//                     onClick={() =>
//                       setFormData((p) => ({
//                         ...p,
//                         description: [
//                           ...p.description,
//                           { heading: "", points: [""] },
//                         ],
//                       }))
//                     }
//                     className="text-sm text-accentTextColor"
//                   >
//                     + Add description section
//                   </button>
//                 </div>
//               </div>

//               <div>
//                 {/* Image Upload */}
//                 <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
//                   Service Image{" "}
//                 </label>
//                 <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
//                   {imagePreview ? (
//                     <div className="relative inline-block">
//                       <img
//                         src={imagePreview}
//                         alt="Preview"
//                         className="mx-auto max-h-48 rounded-lg object-cover"
//                       />

//                       {imageUploading && (
//                         <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
//                           <Loader2
//                             className="animate-spin text-white"
//                             size={32}
//                           />
//                         </div>
//                       )}

//                       {!imageUploading && (
//                         <button
//                           type="button"
//                           onClick={() => {
//                             setImagePreview(null);
//                             setFormData((p) => ({
//                               ...p,
//                               image: null,
//                               imageUrl: "",
//                             }));
//                           }}
//                           className="mt-3 text-sm text-red-600 hover:underline flex items-center gap-1 mx-auto"
//                         >
//                           <Trash2 size={16} /> Remove
//                         </button>
//                       )}
//                     </div>
//                   ) : (
//                     <label className="cursor-pointer block">
//                       <input
//                         type="file"
//                         accept="image/*"
//                         onChange={handleImageChange}
//                         className="hidden"
//                       />
//                       <Upload
//                         size={32}
//                         className="mx-auto mb-3 text-gray-400"
//                       />
//                       <p className="text-sm text-gray-600">
//                         Click to upload image
//                       </p>
//                     </label>
//                   )}
//                 </div>
//               </div>
//               {/* Video Upload */}
//               <div>
//                 <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
//                   Service Video{" "}
//                 </label>
//                 <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
//                   {videoPreview ? (
//                     <div className="relative">
//                       <video
//                         src={videoPreview}
//                         controls
//                         className="mx-auto max-h-64 rounded-lg w-full"
//                       />

//                       {videoUploading && (
//                         <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
//                           <Loader2
//                             className="animate-spin text-white"
//                             size={32}
//                           />
//                         </div>
//                       )}

//                       {!videoUploading && (
//                         <button
//                           type="button"
//                           onClick={() => {
//                             setVideoPreview(null);
//                             setFormData((p) => ({
//                               ...p,
//                               video: null,
//                               videoUrl: "",
//                             }));
//                           }}
//                           className="mt-3 text-sm text-red-600 hover:underline flex items-center gap-1 mx-auto"
//                         >
//                           <Trash2 size={16} /> Remove Video
//                         </button>
//                       )}
//                     </div>
//                   ) : (
//                     <label className="cursor-pointer block">
//                       <input
//                         type="file"
//                         accept="video/*"
//                         onChange={handleVideoChange}
//                         className="hidden"
//                       />
//                       <Video size={32} className="mx-auto mb-3 text-gray-400" />
//                       <p className="text-sm text-gray-600">
//                         Click to upload video
//                       </p>
//                     </label>
//                   )}
//                 </div>
//               </div>
//               {/* Buttons */}
//               <div className="flex gap-4 py-4 border-t sticky bottom-0 bg-white">
//                 <button
//                   type="button"
//                   onClick={onClose}
//                   className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   disabled={imageUploading || videoUploading}
//                   className="relative flex-1 px-6 py-3 bg-primaryBtnColor text-white font-medium rounded-lg hover:bg-primaryBtnHoverColor transition shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
//                 >
//                   {loading ? (
//                     <span className="absolute inset-0 flex items-center justify-center">
//                       <Loader2 className="animate-spin" size={20} />
//                     </span>
//                   ) : isEditMode ? (
//                     "Update Service"
//                   ) : (
//                     "Add Service"
//                   )}
//                 </button>
//               </div>
//             </form>
//           </motion.div>
//         </>
//       )}
//     </AnimatePresence>
//   );
// };

// export default AddServiceDrawer;
